// Shared across every dialog instance — a stacked dialog (e.g. address-book
// opened on top of address-form) must not unlock the page behind the one
// underneath it, so this counts genuinely-open dialogs rather than using a
// single boolean.
let openDialogCount = 0;

function lockBodyScroll() {
  if (openDialogCount === 0) {
    document.documentElement.classList.add('has-modal-open');
  }
  openDialogCount++;
}

function unlockBodyScroll() {
  openDialogCount = Math.max(0, openDialogCount - 1);
  if (openDialogCount === 0) {
    document.documentElement.classList.remove('has-modal-open');
  }
}

export function createModal(dialog, { onBeforeOpen, onClose, closeOnBackdrop = true } = {}) {
  let lastOpener = null;
  // Dialogs we hid when opening — restored when we close. Each instance
  // only restores what it itself obscured, so closing a third-level stack
  // unveils the second-level dialog without disturbing the first.
  let obscured = [];

  function open(opener) {
    lastOpener = opener || null;
    // Also stash the opener on the dialog element so a dialog opened on top
    // of this one (e.g. the address-book on top of the address-form) can
    // walk back up the chain to find the original trigger.
    dialog.__opener = lastOpener;
    // Hide any visible dialogs so the new one stacks cleanly on top — the
    // designer's rule is that the previous popup must not be visible
    // through the new one. The ::backdrop pseudo-element inherits the
    // visibility, so only the topmost dialog's backdrop dims the page.
    obscured = [...document.querySelectorAll('dialog[open]:not(.modal--obscured)')];
    obscured.forEach(d => d.classList.add('modal--obscured'));
    if (onBeforeOpen) onBeforeOpen(opener);
    lockBodyScroll();
    dialog.showModal();
  }

  function close() {
    dialog.close();
  }

  function onDialogClose() {
    unlockBodyScroll();
    lastOpener?.focus();
    if (onClose) onClose();
    dialog.__opener = null;
    obscured.forEach(d => d.classList.remove('modal--obscured'));
    obscured = [];
  }

  // Clicking the ::backdrop fires a click on the dialog itself (e.target
  // === dialog, since nothing inside the visible content was hit). Escape
  // is deliberately left alone here — every dialog still closes on Escape
  // the same way it always has; only the backdrop click is ever gated.
  function onDialogClick(e) {
    if (closeOnBackdrop && e.target === dialog) close();
  }

  function onTriggerClick(e) {
    e.preventDefault();
    open(e.currentTarget);
  }

  function onCloseButtonClick() {
    close();
  }

  dialog.addEventListener('close', onDialogClose);
  dialog.addEventListener('click', onDialogClick);

  // Every dialog's own close (×) button does the same thing — just close —
  // so it's wired here once rather than by each caller. `.modal__back`
  // (address-book's "back to previous dialog" button) is a different,
  // per-dialog behaviour and is deliberately left for the caller to wire.
  const closeBtn = dialog.querySelector('.modal__close');
  if (closeBtn) closeBtn.addEventListener('click', onCloseButtonClick);

  const triggers = dialog.id
    ? document.querySelectorAll(`[data-opens="${CSS.escape(dialog.id)}"]`)
    : [];
  triggers.forEach(t => t.addEventListener('click', onTriggerClick));

  function destroy() {
    dialog.removeEventListener('close', onDialogClose);
    dialog.removeEventListener('click', onDialogClick);
    if (closeBtn) closeBtn.removeEventListener('click', onCloseButtonClick);
    triggers.forEach(t => t.removeEventListener('click', onTriggerClick));
  }

  return { open, close, destroy };
}
